import { Group, Text, ThemeIcon } from '@mantine/core';
import { Icon, IconLock, IconUsers, IconWorld } from '@tabler/icons-react';
import React from 'react';

import {
  AvatarGroup,
  IAvatarGroupItem,
} from 'src/design/molecules/AvatarGroup';

export type AccessKind = 'everyone' | 'onlyYou' | 'people';

export interface IAccessIndicatorProps {
  kind: AccessKind;
  people?: IAvatarGroupItem[];
  peopleLabel?: string;
}

const Icons: Record<AccessKind, Icon> = {
  everyone: IconWorld,
  onlyYou: IconLock,
  people: IconUsers,
};

const defaultPeopleLabel = (count: number) => {
  if (count === 0) return 'Shared with specific people';
  return count === 1 ? 'Shared with 1 person' : `Shared with ${count} people`;
};

/** The label AccessIndicator renders, for callers that need the same wording outside the indicator (a tooltip, an aria-label). */
export const accessIndicatorLabel = ({
  kind,
  people,
  peopleLabel,
}: IAccessIndicatorProps): string => {
  if (kind === 'everyone') return 'Anyone with job access';
  if (kind === 'onlyYou') return 'Only you';
  return peopleLabel ?? defaultPeopleLabel(people?.length ?? 0);
};

/** @registry Neutral inline "who can see this" row: gray icon + dimmed label + optional AvatarGroup; use instead of a coloured Alert for access/sharing state. */
export const AccessIndicator: React.FC<IAccessIndicatorProps> = ({
  kind,
  people,
  peopleLabel,
}) => {
  const Icon = Icons[kind];
  const label = accessIndicatorLabel({ kind, people, peopleLabel });

  return (
    <Group gap="xs" wrap="nowrap">
      <ThemeIcon variant="light" color="gray" size="sm">
        <Icon size={14} />
      </ThemeIcon>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      {people && people.length > 0 && (
        <AvatarGroup items={people} maxItems={4} />
      )}
    </Group>
  );
};
